import styled from 'styled-components';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import IconExplosion from '../components/IconExplosion';

gsap.registerPlugin(ScrollTrigger);

const HomeContainer = styled(motion.div)`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-image: url('/images/bg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.25); // Ajout d'un overlay sombre pour améliorer la lisibilité
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
`;

const Title = styled(motion.h1)`
  font-size: clamp(3rem, 8vw, 6rem);
  font-family: ${props => props.theme.fonts.heading};
  color: white;
  margin-bottom: ${props => props.theme.spacing.md};
  line-height: 1.2;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: clamp(2.5rem, 6vw, 3.5rem);
  }
`;

const Subtitle = styled(motion.h2)`
  font-size: clamp(1.2rem, 3vw, 2rem);
  color: white;
  font-family: ${props => props.theme.fonts.heading};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: clamp(1rem, 4vw, 1.5rem);
    margin-bottom: ${props => props.theme.spacing.lg};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 3rem;
  justify-content: center;
  width: 100%;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    padding: 0 1rem;
  }
`;

const BaseButton = styled(motion.button)`
  position: relative;
  padding: 1.2rem 3rem;
  min-width: 260px;
  font-family: ${props => props.theme.fonts.heading};
  font-size: 1.1rem;
  border: none;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    transition: all 0.5s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 60%);
    transform: scale(0);
    transition: transform 0.5s ease;
    z-index: -1;
  }

  &:hover::after {
    transform: scale(1) translate(25%, 25%);
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: ${props => props.theme.colors.wood};
  color: ${props => props.theme.colors.cream};
  border-radius: ${props => props.theme.borderRadius.medium};

  &::before {
    background: linear-gradient(
      45deg,
      ${props => props.theme.colors.woodLight} 0%,
      ${props => props.theme.colors.wood} 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(139, 69, 19, 0.3);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(1px);
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: transparent;
  color: #F5E6D3;
  border: 2px solid #F5E6D3;
  border-radius: ${props => props.theme.borderRadius.medium};
  backdrop-filter: blur(5px);
  
  &::before {
    background: #F5E6D3;
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.5s ease;
  }

  &:hover {
    color: black;
    border-color: transparent;
    
    &::before {
      transform: scaleX(1);
      transform-origin: left;
    }
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ScrollContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 16vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    bottom: 20vh;
  }
`;

const ScrollText = styled(motion.span)`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 0.9rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: #F5E6D3;
  margin-bottom: 0.5rem;
`;

const ArrowIcon = styled(motion.div)`
  position: relative;
  width: 20px;
  height: 20px;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-style: solid;
    border-width: 0 2px 2px 0;
    border-color: #F5E6D3;
    transform: rotate(45deg);
    transition: all 0.3s ease;
  }

  &:hover:before {
    transform: rotate(45deg) scale(1.1);
    border-color: ${props => props.theme.colors.woodDark};
  }
`;

const ContentSection = styled(motion.section)`
  min-height: 100vh;
  width: 100%;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${props => props.theme.colors.cream};
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-height: auto;
    padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.md};
  }
`;

const ContentText = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  padding: ${props => props.theme.spacing.xl} 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 150px;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => props.theme.colors.wood},
      transparent
    );
    left: 50%;
    transform: translateX(-50%);
  }

  &::before {
    top: 0;
  }

  &::after {
    bottom: 0;
  }
`;

const TextLine = styled(motion.p)`
  font-size: clamp(1rem, 2vw, 1.2rem);
  line-height: 1.8;
  color: ${props => props.theme.colors.black};
  margin-bottom: 1.5rem;
  font-family: ${props => props.theme.fonts.heading};
  font-weight: 300;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
    line-height: 1.5;
    padding: 0 1rem;
    margin-bottom: 1rem;
  }
`;

const FeaturesGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
    padding: 0 ${props => props.theme.spacing.sm};
    margin: 1rem auto;
  }
`;

const FeatureContent = styled(motion.div)`
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.spacing.xs};
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.wood};
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FeatureCard = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: visible;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.lg};
  }
`;

const FeatureTitle = styled(motion.h3)`
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  font-family: ${props => props.theme.fonts.heading};
  color: ${props => props.theme.colors.black};
  margin-bottom: ${props => props.theme.spacing.sm};
  position: relative;
  padding-bottom: ${props => props.theme.spacing.xs};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
    margin: 0;
    padding: 0;
    color: ${props => props.theme.colors.wood};
  }
`;

const FeatureDescription = styled(motion.p)`
  color: ${props => props.theme.colors.black};
  line-height: 1.6;
  font-size: clamp(0.9rem, 2vw, 1rem);
  max-width: 280px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    line-height: 1.4;
    margin: 0;
    max-width: none;
    opacity: 0.9;
  }
`;

const Card3D = {
  handleMouseMove: (e, target) => {
    const card = target;
    const { left, top, width, height } = card.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    const rotateX = ((mouseY / height) * -15).toFixed(2);
    const rotateY = ((mouseX / width) * 15).toFixed(2);
    
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  },

  handleMouseLeave: (target) => {
    target.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
  }
};

const arrowVariants = {
  initial: { y: 0 },
  animate: {
    y: [0, 10, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const textVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: [0.4, 1, 0.4],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.8,
      when: "beforeChildren",
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.2,
      yoyo: Infinity
    }
  }
};

const Logo = styled.img`
  height: 50px;
  width: auto;
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1000;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: 40px;
    top: 15px;
    left: 15px;
  }
`;

const VideoSection = styled.section`
  padding: 6rem 0;
  background: ${props => props.theme.colors.cream};
  text-align: center;
`;

const VideoContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const ResponsiveVideoWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  height: 0;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.large};
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const VideoTitle = styled(motion.h2)`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.wood};
  margin-bottom: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

function Home() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const titleRef = useRef(null);
  const [contentRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [cards, setCards] = useState([
    { id: 1, title: "Compositions Originales", icon: "music", description: "Des créations uniques qui racontent des histoires authentiques et touchantes." },
    { id: 2, title: "Folk Acoustique", icon: "guitar", description: "Un son chaleureux et intime porté par des arrangements acoustiques soignés." },
    { id: 3, title: "Harmonies Vocales", icon: "microphone", description: "Des voix qui s'entremêlent pour créer des moments d'émotion pure." }
  ]);

  const handleMouseMove = (e, cardElement) => {
    if (window.innerWidth <= 768) return;
    
    const rect = cardElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = (y - centerY) / 20;
    const rotateY = -(x - centerX) / 20;

    cardElement.style.transform = `
      perspective(1000px)
      rotateX(${rotateX}deg)
      rotateY(${rotateY}deg)
      scale3d(1.05, 1.05, 1.05)
    `;
  };

  const handleMouseLeave = (cardElement) => {
    cardElement.style.transform = `
      perspective(1000px)
      rotateX(0deg)
      rotateY(0deg)
      scale3d(1, 1, 1)
    `;
  };

  // Parallax effect pour le titre
  const titleY = useTransform(scrollY, [0, 500], [0, 200]);
  const opacity = useTransform(scrollY, [0, 200], [1, 0]);

  useEffect(() => {
    // Animation GSAP pour le scroll
    const sections = gsap.utils.toArray('.content-section');
    
    sections.forEach((section) => {
      ScrollTrigger.create({
        trigger: section,
        start: 'top center',
        end: 'bottom center',
        toggleClass: 'active',
        onEnter: () => {
          gsap.to(section, {
            y: 0,
            opacity: 1,
            duration: 1,
            ease: 'power3.out'
          });
        }
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Logo 
        src="/icon_web.png" 
        alt="Anna Sanz Logo" 
        onClick={() => navigate('/')}
      />
      <HeroSection>
        <HeroContent>
          <Title variants={itemVariants}>
            Anna Sanz
          </Title>
          <Subtitle variants={itemVariants}>
            Musique Folk & Compositions Originales
          </Subtitle>
          <ButtonContainer>
            <PrimaryButton
              as={motion.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/music')}
            >
              Découvrir notre musique
            </PrimaryButton>
            
            <SecondaryButton
              as={motion.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/contact')}
            >
              Nous contacter
            </SecondaryButton>
          </ButtonContainer>
        </HeroContent>
        
        <ScrollContainer>
          <ScrollText
            initial="initial"
            animate="animate"
            variants={textVariants}
          >
            DÉCOUVRIR
          </ScrollText>
          <motion.div
            initial="initial"
            animate="animate"
            variants={arrowVariants}
            onClick={() => {
              window.scrollTo({
                top: window.innerHeight,
                behavior: 'smooth'
              });
            }}
            style={{ cursor: 'pointer' }}
          >
            <ArrowIcon />
          </motion.div>
        </ScrollContainer>
      </HeroSection>

      <motion.div
        ref={contentRef}
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="content-section"
      >
        <ContentText>
          <TextLine variants={itemVariants}>
            Bienvenue dans l'univers musical d'Anna Sanz
          </TextLine>
          <TextLine variants={itemVariants}>
            où la chaleur des mélodies folk se mêle à la poésie des compositions originales.
          </TextLine>
          <TextLine variants={itemVariants}>
            Notre trio acoustique vous invite à un voyage musical intime et authentique,
          </TextLine>
          <TextLine variants={itemVariants}>
            tissant des histoires à travers des arrangements délicats et des harmonies vocales envoûtantes.
          </TextLine>
        </ContentText>

        <FeaturesGrid>
          {cards.map((card) => (
            <FeatureCard
              key={card.id}
              onMouseMove={(e) => Card3D.handleMouseMove(e, e.currentTarget)}
              onMouseLeave={(e) => Card3D.handleMouseLeave(e.currentTarget)}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <IconExplosion 
                color={theme.colors.wood}
                minSpeed={80}
                maxSpeed={200}
              >
                <FeatureIcon>
                  <i className={`fas fa-${card.icon}`}></i>
                </FeatureIcon>
              </IconExplosion>
              <FeatureContent>
                <FeatureTitle>{card.title}</FeatureTitle>
                <FeatureDescription>{card.description}</FeatureDescription>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </motion.div>

       <VideoSection>
        <VideoContainer>
          <VideoTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Découvrez notre univers
          </VideoTitle>
          <ResponsiveVideoWrapper>
            <iframe
              src="https://www.youtube.com/embed/KeITWV30qBg?si=GvNS4u2R6ss24ueV"
              title="Anna Sanz - Vidéo de présentation"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </ResponsiveVideoWrapper>
        </VideoContainer>
      </VideoSection>
    </motion.div>
  );
}

export default Home;
